import { TFunction } from 'i18next'
import get from 'lodash/fp/get'

import { Attribute, EntityItem, Order } from 'api/ApiTypes'

import ContactSupport from '../components/shared/ContactSupport'
import RequestDocuments from '../components/shared/order-opportunity/RequestDocuments'
import { ExtraGroup } from '../components/shared/renderers/attributes/AttributesRenderer'
import { Contact, RepeatableAddress } from '../context/ContactContext'
import useEcpTranslation from '../hooks/common/useEcpTranslation'
import { useDocumentPermissions } from '../hooks/documents/useDocumentPermissions'

import { RequestStatus } from './CommonUtils'
import { EntityType } from './EntityUtils'
import { Address } from './OrderUtils'
import { formatBillingAmount, RepeatablePaymentOption } from './PaymentUtils'

// For those attributes which need to be combined and show in a one row, like amount and unit
export const COMBINED_ATTRIBUTES = new Map([
  ['renewal_duration_amount', 'renewal_duration_unit'],
  ['notice_time_amount', 'notice_time_unit'],
  ['termination_time_amount', 'termination_time_unit'],
  ['billing_duration_amount', 'billing_period'],
  ['installment_amount', 'installment_amount_currency'],
  ['balance', 'installment_amount_currency']
])

export const HIDDEN_ATTRIBUTES = {
  contact: [
    'consent_print_marketing',
    'consent_phone_call',
    'consent_sms_marketing',
    'consent_email_marketing',
    'contact_owner',
    'account',
    'portal_users'
  ],
  opportunity: ['purchased_prices'],
  order: ['order_number', 'source'],
  meter: ['counters', 'contract'],
  contract: [
    // Already represented in the UI
    'status',

    // Part of other combined fields
    'billing_duration_unit',
    'notice_time_unit',
    'renewal_duration_unit',
    'billing_period',
    'termination_time_unit',

    // Already represented in the UI
    'billing_due_day',
    'installment_amount',
    'installment_amount_currency',
    'balance',
    'balance_currency',

    'order',
    'meters',

    // Not relevant for end customers, but used internally
    'move_in_date',
    'move_out_date'
  ]
}

const translateAttributeLabel = (
  t: TFunction,
  attributeLabel: string
): string => {
  if (t(`attributes.${attributeLabel}`).includes('attributes.')) {
    return attributeLabel
  }

  return t(`attributes.${attributeLabel}`)
}

const getStringValueOfAttribute = (attributeValue: any) => {
  if (typeof attributeValue === 'string') {
    return attributeValue
  }

  return attributeValue?.toString()
}

/*
 This function is responsible for handling the combination of two
 attributes value, if it is in the COMBINED_ATTRIBUTES
*/
const getAttributeValueByType = (
  entity: EntityItem,
  attribute: Attribute,
  t: TFunction
) => {
  if (COMBINED_ATTRIBUTES.has(attribute.name)) {
    const currencyValue = (get(attribute.name)(entity) / 100).toFixed(2)
    const formattedValue = formatBillingAmount(currencyValue)

    const firstValue: string =
      getStringValueOfAttribute(
        attribute.type === 'currency'
          ? formattedValue
          : get(attribute.name)(entity)
      ) || ''

    const secondValue: string =
      getStringValueOfAttribute(
        get(COMBINED_ATTRIBUTES.get(attribute.name))(entity)
      ) || ''

    if (!secondValue) {
      // First value alone usually makes sense
      return firstValue
    } else if (!firstValue) {
      // Second value alone (unit, currency, etc.) alone not so much
      return null
    }

    return `${firstValue} ${translateAttributeLabel(t, secondValue)}`
  }

  const allSecondValues: string[] = Array.from(COMBINED_ATTRIBUTES.values())

  // skip the value if it's already combined with something else
  if (allSecondValues.includes(attribute.name)) {
    return null
  }

  // normal(aka single) value
  return get(attribute.name)(entity)
}

interface AddressRelation {
  entity_id: string
  path: string
  _id?: string
  _tags?: string[]
}

const findValueFromRelations = (
  relationDetail: any,
  relations: EntityItem[]
): RepeatableAddress[] | Contact[] | RepeatablePaymentOption | Address[] => {
  const attributeRelations: AddressRelation[] =
    relationDetail?.$relation_ref || relationDetail?.$relation

  if (!attributeRelations?.length || !relations?.length) {
    return null
  }

  return attributeRelations
    .map((attributeRelation: AddressRelation) => {
      const respectiveEntity = relations.find(
        (relation) => relation?._id === attributeRelation?.entity_id
      )

      if (!respectiveEntity) {
        return null
      }

      if (!attributeRelation?.path) {
        return respectiveEntity
      }

      const relationValue: any[] = get(attributeRelation.path)(respectiveEntity)

      if (!relationValue?.length) {
        return null
      }

      if (attributeRelation?._id) {
        return (
          relationValue.find(
            (relation) => relation?._id === attributeRelation._id
          ) || null
        )
      }

      if (Array.isArray(relationValue)) {
        return relationValue?.[0] || null
      }

      return relationValue
    })
    .filter(Boolean)
}

export const useExtraGroups = (
  entityId: string,
  entityType: EntityType,
  files?: EntityItem[]
) => {
  const { t } = useEcpTranslation()
  const { isViewDocumentPermitted } = useDocumentPermissions()

  const groups: ExtraGroup[] = []
  const supportGroup = {
    name: t('common.support'),
    label: t('common.support'),
    component: <ContactSupport />,
    greyBackground: true
  }

  // Just show support if there are no files
  if (entityType === EntityType.METER) {
    return [supportGroup]
  }

  if (isViewDocumentPermitted) {
    groups.push({
      name: t('my_orders.documents'),
      label: `${t('my_orders.documents')} (${files?.length})`,
      component: (
        <RequestDocuments
          entityId={entityId}
          entityType={entityType}
          files={files}
        />
      )
    })
  }

  // Do not show support in My Account page
  if (entityType === EntityType.CONTACT) {
    return groups
  }

  groups.push(supportGroup)

  return groups
}

const hasRelationValue = (
  attributeType: string,
  attributeValue: any,
  relations: EntityItem[]
): boolean => {
  if (attributeType.startsWith('relation')) {
    const relationValue = findValueFromRelations(attributeValue, relations)

    return !!relationValue?.[0]
  }

  return true
}

const isValidOrderToDisplay = (contacts: Contact[], order: Order): boolean => {
  if (order?.status === RequestStatus.Draft || !contacts?.length) {
    return false
  }

  const owner: Contact = contacts?.find(
    (contact: Contact) =>
      contact._id === order?.billing_contact?.$relation?.[0]?.entity_id
  )

  return Boolean(owner)
}

interface ApprovalStateAttributes {
  [key: string]: string[]
}

const needsApproval = (
  attributeName: string,
  schema: string,
  approvalStateAttributes: ApprovalStateAttributes
): boolean => {
  return approvalStateAttributes?.[schema]?.includes(attributeName)
}

export default {
  translateAttributeLabel,
  getAttributeValueByType,
  findValueFromRelations,
  hasRelationValue,
  isValidOrderToDisplay,
  needsApproval
}
